import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { BbAiThoughtsScroll } from '../bb-ai-thoughts-scroll/bb-ai-thoughts-scroll';

/** Cadence for cycling status copy (faster when reduced-motion is on). */
export const AI_DRAFT_STATUS_MS = 2200;
export const AI_DRAFT_STATUS_MS_REDUCED = 900;

export const DEFAULT_AI_DRAFT_PHRASES = [
  'Tuning into your brief',
  'Hunting for the hook',
  'Sketching the concepts',
  "Writing the do's & don'ts",
  'Dialling in the audience',
  'Pulling it all together',
] as const;

export const SCREENING_AI_DRAFT_PHRASES = [
  'Reading your screening brief',
  'Scanning for red flags',
  'Drafting the requirements',
  'Checking brand-safety cues',
  'Pulling it all together',
] as const;

/** Full-stage loader shown while SHOUT AI streams a brief draft. */
@Component({
  selector: 'bb-ai-drafting-loader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbAiThoughtsScroll],
  templateUrl: './bb-ai-drafting-loader.html',
  styleUrl: './bb-ai-drafting-loader.scss',
  host: {
    class: 'block h-full',
  },
})
export class BbAiDraftingLoader {
  private readonly destroyRef = inject(DestroyRef);

  readonly heading = input('SHOUT AI is drafting your brief…');
  readonly thoughts = input('');
  readonly isCompiling = input(false);
  readonly compilingLabel = input('Finalising your brief…');
  readonly statusPhrases = input<readonly string[]>(DEFAULT_AI_DRAFT_PHRASES);

  readonly sparkles = [0, 1, 2, 3, 4, 5] as const;

  private readonly phraseIndex = signal(0);

  readonly statusLine = computed(() => {
    if (this.isCompiling()) {
      return this.compilingLabel();
    }
    const phrases = this.statusPhrases();
    return phrases[this.phraseIndex() % phrases.length] ?? phrases[0];
  });

  constructor() {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stepMs = reduce ? AI_DRAFT_STATUS_MS_REDUCED : AI_DRAFT_STATUS_MS;
    const timer = setInterval(() => {
      if (!this.isCompiling()) {
        this.phraseIndex.update((i) => i + 1);
      }
    }, stepMs);

    this.destroyRef.onDestroy(() => clearInterval(timer));
  }
}
